<template> 
  <div>
    <div v-if="submitted && (mode === 'public' || mode === 'modal')" class="text-center py-8">
      <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/20 text-success mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 class="text-2xl font-bold text-base-content mb-2">{{ t('contacts.form.publicSuccess.title') }}</h3>
      <p class="text-base-content/70 mb-6">{{ t('contacts.form.publicSuccess.message') }}</p>
      <div class="flex flex-col sm:flex-row gap-2 justify-center">
        <button v-if="mode === 'modal'" type="button" class="btn btn-primary" @click="handleClose">
          {{ t('contacts.modal.close') }}
        </button>
        <button v-else type="button" class="btn btn-primary" @click="resetForm">
          {{ t('contacts.form.publicSuccess.submitAnother') }}
        </button>
        <button v-if="mode === 'modal'" type="button" class="btn btn-outline" @click="handleSubmitAnother">
          {{ t('contacts.form.publicSuccess.submitAnother') }}
        </button>
      </div>
    </div>

    <form v-else class="w-full max-w-3xl mx-auto">
      <div class="alert alert-error shadow-lg my-4" v-if="contactsError">
        <span>{{ contactsError }}</span>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('contacts.form.name') }}</span>
            <span class="label-text-alt text-error" v-if="errorContact_name">{{ t('common.requiredMark') }}</span>
          </label> 
          <input 
            type="text" 
            :placeholder="t('contacts.form.namePlaceholder')" 
            v-model="contact_name" 
            class="input input-bordered w-full"
          >
          <label class="label" v-if="errorContact_name">
            <span class="label-text-alt text-error">{{ errorContact_name }}</span>
          </label>
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('contacts.form.email') }}</span>
            <span class="label-text-alt text-error" v-if="errorContact_email">{{ t('common.requiredMark') }}</span>
          </label> 
          <input 
            type="email"
            :placeholder="t('contacts.form.emailPlaceholder')" 
            v-model="contact_email" 
            class="input input-bordered w-full"
          >
          <label class="label" v-if="errorContact_email">
            <span class="label-text-alt text-error">{{ errorContact_email }}</span>
          </label>
        </div>
      </div>

      <div class="form-control mt-4">
        <label class="label">
          <span class="label-text font-medium">{{ t('contacts.form.subject') }}</span>
          <span class="label-text-alt text-error" v-if="errorContact_subject">{{ t('common.requiredMark') }}</span>
        </label> 
        <input 
          type="text"
          :placeholder="t('contacts.form.subjectPlaceholder')" 
          v-model="contact_subject" 
          class="input input-bordered w-full"
        >
        <label class="label" v-if="errorContact_subject">
          <span class="label-text-alt text-error">{{ errorContact_subject }}</span>
        </label>
      </div>

      <div class="form-control mt-4">
        <label class="label">
          <span class="label-text font-medium">{{ t('contacts.form.message') }}</span>
          <span class="label-text-alt text-error" v-if="errorContact_message">{{ t('common.requiredMark') }}</span>
        </label> 
        <textarea
          :placeholder="t('contacts.form.messagePlaceholder')" 
          v-model="contact_message" 
          rows="6"
          class="textarea textarea-bordered w-full"
        ></textarea>
        <label class="label" v-if="errorContact_message">
          <span class="label-text-alt text-error">{{ errorContact_message }}</span>
        </label>
      </div>

      <div class="flex justify-end gap-2 mt-8">
        <button
          v-if="mode === 'modal'"
          type="button"
          class="btn btn-ghost"
          @click="handleClose"
        >
          {{ t('common.actions.cancel') }}
        </button>
        <button 
          type="button" 
          @click="save" 
          class="btn btn-primary gap-2" 
          :class="contactsLoading && 'loading btn-disabled'"
        >
          {{ t('contacts.form.submit') }}
        </button>
      </div>
    </form>    
  </div>   
</template>


<script>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import useCreate from './createValidate';
import { useRouter } from 'vue-router';
import useContacts from '../../../../compositions/useModelContacts';

export default {
    emits: ['close'],
    props: {
        mode: {
            type: String,
            default: 'admin',
            validator: (value) => ['admin', 'public', 'modal'].includes(value),
        },
    },
    setup(props, { emit }) {
        const { t } = useI18n();
        const createForm = useCreate();
        const { validate, values, setValues, resetForm: resetValidation } = createForm;
        const router = useRouter();
        const { loading: contactsLoading, error: contactsError, addItem } = useContacts();
        const submitted = ref(false);

        onMounted(() => {
          setValues({ contact_status: 'new' });
        });

        const resetForm = () => {
          submitted.value = false;
          resetValidation();
          setValues({ contact_status: 'new' });
        };

        const handleClose = () => {
          resetForm();
          emit('close');
        };

        const handleSubmitAnother = () => {
          resetForm();
        };

        const save = () => {
            validate().then((validateSuccess) => {
                if (!validateSuccess.valid) {
                  console.log('Check the form.', errors.value);
                  return;
                }

                addItem({ ...values, keywords: true, contact_status: 'new' }).then((response) => {
                    if (props.mode === 'public' || props.mode === 'modal') {
                        submitted.value = true;
                    } else {
                        router.push(`/contacts/detail/${response.id}`);
                    }
                });
            }).catch((validateError) => {
                console.log(validateError);
            });
        };

        return {
            ...createForm,
            save,
            contactsLoading,
            contactsError,
            submitted,
            resetForm,
            handleClose,
            handleSubmitAnother,
            t,
        };
    },
};
</script>
